import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Box, useMediaQuery } from '@mui/material';
import { Outlet } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { GripVertical } from 'lucide-react';
import { Navbar } from '../../components/Navbar';
import { Sidebar } from '../../components/Sidebar';
import { RootState } from '../../state/store'; // Import RootState
import { useWhatsAppData } from '../../hooks/useWhatsAppData'; // Import WhatsApp data hook

/** Viewports below this width keep the sidebar collapsed by default (global layout). */
const SIDEBAR_AUTO_EXPAND_MIN_WIDTH_PX = 1200;

/** Persists the user's explicit expand/collapse choice across reloads. */
const SIDEBAR_STATE_STORAGE_KEY = 'st-ck:sidebar-open';

/** Persists the user's sidebar width across reloads. */
const SIDEBAR_WIDTH_STORAGE_KEY = 'st-ck:sidebar-width';

/** Persists icon-only (minified) rail vs full icon+label sidebar. Independent of open/closed. */
const SIDEBAR_MINIFIED_STORAGE_KEY = 'st-ck:sidebar-minified';

const SIDEBAR_MIN_WIDTH = 200;
const SIDEBAR_MAX_WIDTH = 480;
const SIDEBAR_DEFAULT_WIDTH = 250;
/** Icon-only rail: parent icons + a little indent for expanded children. */
const SIDEBAR_MINIFIED_WIDTH = 80;

function clampSidebarWidth(width: number): number {
  return Math.min(SIDEBAR_MAX_WIDTH, Math.max(SIDEBAR_MIN_WIDTH, width));
}

function readStoredSidebarWidth(): number | null {
  try {
    const raw = localStorage.getItem(SIDEBAR_WIDTH_STORAGE_KEY);
    if (raw === null) return null;
    const parsed = Number.parseInt(raw, 10);
    if (Number.isNaN(parsed)) return null;
    return clampSidebarWidth(parsed);
  } catch {
    return null;
  }
}

function getInitialDrawerWidth(): number {
  if (typeof window === 'undefined') return SIDEBAR_DEFAULT_WIDTH;
  const stored = readStoredSidebarWidth();
  return stored !== null ? stored : SIDEBAR_DEFAULT_WIDTH;
}

function readStoredSidebarOpen(): boolean | null {
  try {
    const raw = localStorage.getItem(SIDEBAR_STATE_STORAGE_KEY);
    if (raw === 'true') return true;
    if (raw === 'false') return false;
    return null;
  } catch {
    return null;
  }
}

function getInitialSidebarOpen(): boolean {
  if (typeof window === 'undefined') return true;
  // Narrow viewports always start collapsed regardless of the saved preference.
  if (window.innerWidth < SIDEBAR_AUTO_EXPAND_MIN_WIDTH_PX) return false;
  const stored = readStoredSidebarOpen();
  return stored !== null ? stored : true;
}

function readStoredSidebarMinified(): boolean {
  try {
    return localStorage.getItem(SIDEBAR_MINIFIED_STORAGE_KEY) === 'true';
  } catch {
    return false;
  }
}

export const Layout = () => {
  const isNonMobile = useMediaQuery('(min-width: 600px)');
  const isWideLayout = useMediaQuery(`(min-width: ${SIDEBAR_AUTO_EXPAND_MIN_WIDTH_PX}px)`);
  const [isSidebarOpen, setIsSidebarOpen] = useState(getInitialSidebarOpen);
  const [isSidebarMinified, setIsSidebarMinified] = useState(readStoredSidebarMinified);
  const [drawerWidth, setDrawerWidth] = useState(getInitialDrawerWidth);
  const [isResizing, setIsResizing] = useState(false);
  const isResizingRef = useRef(false);
  const user = useSelector((state: RootState) => state.authentication.user); // Get user from Redux store
  const isSuperAdmin = user?.role === 'superadmin'; // Determine if user is superadmin
  
  // Initialize WhatsApp data globally to ensure auto-refresh works
  useWhatsAppData();

  const handleResizeMouseDown = useCallback((event: React.MouseEvent) => {
    event.preventDefault();
    isResizingRef.current = true;
    setIsResizing(true);
    document.body.style.userSelect = 'none';
    document.body.style.cursor = 'col-resize';
  }, []);

  useEffect(() => {
    const handleMouseMove = (event: MouseEvent) => {
      if (!isResizingRef.current) return;
      setDrawerWidth(clampSidebarWidth(event.clientX));
    };

    const handleMouseUp = () => {
      if (!isResizingRef.current) return;
      isResizingRef.current = false;
      setIsResizing(false);
      document.body.style.userSelect = '';
      document.body.style.cursor = '';
      setDrawerWidth((currentWidth) => {
        try {
          localStorage.setItem(SIDEBAR_WIDTH_STORAGE_KEY, String(currentWidth));
        } catch {
          /* ignore quota / serialization errors — persistence is best-effort */
        }
        return currentWidth;
      });
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
      document.body.style.userSelect = '';
      document.body.style.cursor = '';
    };
  }, []);

  // User-triggered toggles go through this wrapper so the explicit choice is
  // persisted; the responsive effect below uses the raw setter so transient
  // (viewport-driven) collapses never overwrite the saved preference.
  const handleSetSidebarOpen = React.useCallback<React.Dispatch<React.SetStateAction<boolean>>>(
    (value) => {
      setIsSidebarOpen((prev) => {
        const next = typeof value === 'function'
          ? (value as (p: boolean) => boolean)(prev)
          : value;
        try {
          localStorage.setItem(SIDEBAR_STATE_STORAGE_KEY, String(next));
        } catch {
          /* ignore quota / serialization errors — persistence is best-effort */
        }
        return next;
      });
    },
    [],
  );

  const handleSetSidebarMinified = React.useCallback<React.Dispatch<React.SetStateAction<boolean>>>(
    (value) => {
      setIsSidebarMinified((prev) => {
        const next = typeof value === 'function'
          ? (value as (p: boolean) => boolean)(prev)
          : value;
        try {
          localStorage.setItem(SIDEBAR_MINIFIED_STORAGE_KEY, String(next));
        } catch {
          /* ignore quota / serialization errors — persistence is best-effort */
        }
        return next;
      });
    },
    [],
  );

  // Collapse the sidebar on viewports under 1200px (responsive, not persisted);
  // on wide layouts restore the user's saved preference (defaults to expanded).
  useEffect(() => {
    if (!isNonMobile || !isWideLayout) {
      setIsSidebarOpen(false);
    } else {
      const stored = readStoredSidebarOpen();
      setIsSidebarOpen(stored !== null ? stored : true);
    }
  }, [isNonMobile, isWideLayout]);

  const effectiveDrawerWidth = isSidebarOpen && isSidebarMinified ? SIDEBAR_MINIFIED_WIDTH : drawerWidth;
  const showResizeHandle = isNonMobile && isSidebarOpen && !isSidebarMinified;

  return (
    <Box display="flex" width="100%" height="100%" position="relative">
        <Sidebar 
          setIsSidebarOpen={handleSetSidebarOpen} 
          isSidebarOpen={isSidebarOpen}
          isSidebarMinified={isSidebarMinified}
          setIsSidebarMinified={handleSetSidebarMinified}
          drawerWidth={effectiveDrawerWidth}
          isNonMobile={isNonMobile}
          isSuperAdmin={isSuperAdmin} // Pass isSuperAdmin to Sidebar
        />

        {showResizeHandle && (
          <Box
            role="separator"
            aria-orientation="vertical"
            aria-label="Resize sidebar"
            onMouseDown={handleResizeMouseDown}
            sx={{
              position: 'absolute',
              left: effectiveDrawerWidth - 3,
              top: 0,
              bottom: 0,
              width: 6,
              cursor: 'col-resize',
              zIndex: (theme) => theme.zIndex.drawer + 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              '&:hover .sidebar-resize-grip, &.is-resizing .sidebar-resize-grip': {
                opacity: 1,
              },
            }}
            className={isResizing ? 'is-resizing' : undefined}
          >
            <Box
              className="sidebar-resize-grip"
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 20,
                height: 40,
                borderRadius: 1,
                backgroundColor: 'background.paper',
                border: 1,
                borderColor: 'divider',
                boxShadow: 1,
                opacity: isResizing ? 1 : 0,
                transition: 'opacity 0.15s ease-in-out',
                pointerEvents: 'none',
              }}
            >
              <GripVertical size={16} />
            </Box>
          </Box>
        )}
        
        {/* Main content area - spans full width */}
        <Box sx={{ 
          flex: 1,
          minWidth: 0, // Prevents flex item from overflowing
          position: 'relative' // For proper positioning
        }}>
          {/* Navbar - spans full width */}
          <Navbar 
            setIsSidebarOpen={handleSetSidebarOpen} 
            isSidebarOpen={isSidebarOpen}
            isNonMobile={isNonMobile}
          />
          
          {/* Content area - responsive padding for better visual spacing */}
          <Box sx={{ 
            pt: 2,
            pr: 2,
            pb: 2,
            pl: { xs: 1.25, sm: 2.5 }, // 10px on mobile, 20px on desktop
            overflowY: 'auto', 
            height: 'calc(100vh - 100px)',
            width: '100%'
          }}>
            <Outlet/>
          </Box>
        </Box>
    </Box>
  )
};
